<template>
  <Modal v-model:visible="showModal" title="未翻译字段收集" width="700px" :footer="null" destroyOnClose>
    <div class="collector-content">
      <div class="stats-bar">
        <Statistic title="已收集Key" :value="totalCount" />
        <Divider type="vertical" />
        <Statistic title="已翻译" :value="translatedCount" :value-style="{ color: '#52c41a' }" />
        <Divider type="vertical" />
        <Statistic title="未翻译" :value="untranslatedCount" :value-style="{ color: '#ff4d4f' }" />
      </div>

      <div class="action-bar">
        <Space>
          <Button @click="handleClear" danger size="small">
            <ClearOutlined />
            清空
          </Button>
          <Button @click="handleRefresh" size="small">
            <ReloadOutlined />
            刷新
          </Button>
        </Space>
        <Space>
          <Button @click="handleCopy" size="small">
            <CopyOutlined />
            复制未翻译
          </Button>
          <Popconfirm
            title="确认上报"
            :content="`将上报 ${untranslatedCount} 个未翻译字段到飞书`"
            @confirm="handleReport"
            ok-text="确定"
            cancel-text="取消"
          >
            <Button type="primary" size="small" :loading="reporting">
              <SendOutlined />
              上报飞书
            </Button>
          </Popconfirm>
        </Space>
      </div>

      <Alert
        v-if="!hasWebhook"
        message="提示"
        description="未配置 VITE_FEISHU_WEBHOOK_URL 环境变量，无法上报到飞书"
        type="warning"
        show-icon
        class="mb-16"
      />

      <div class="list-section">
        <Tabs v-model:activeKey="activeTab">
          <TabPane key="untranslated" :tab="`未翻译 (${untranslatedCount})`"></TabPane>
          <TabPane key="translated" :tab="`已翻译 (${translatedCount})`"></TabPane>
        </Tabs>

        <div v-if="activeTab === 'untranslated'" class="key-list">
          <Empty v-if="untranslatedKeys.length === 0" description="暂无未翻译字段" />
          <div v-else class="key-items">
            <div v-for="key in untranslatedKeys" :key="key" class="key-item">
              <span class="key-text">{{ key }}</span>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'translated'" class="key-list">
          <Empty v-if="translatedKeys.length === 0" description="暂无已翻译字段" />
          <div v-else class="key-items">
            <div v-for="key in translatedKeys" :key="key" class="key-item translated">
              <span class="key-text">{{ key }}</span>
              <CheckCircleOutlined class="check-icon" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script lang="ts" setup>
  import { ref, computed } from 'vue'
  import { Modal, Button, Tabs, Statistic, Divider, Space, Popconfirm, Alert, Empty, message } from 'ant-design-vue'
  import { ClearOutlined, ReloadOutlined, CopyOutlined, SendOutlined, CheckCircleOutlined } from '@ant-design/icons-vue'
  import { translateCollector } from '@jms/utils/translateCollector'
  import { useSettingStore } from '@jms/store'
  // import FeishuService from '@/service/api/feishu'

  const { VITE_FEISHU_WEBHOOK_URL, VITE_COUNTRY } = import.meta.env

  const props = defineProps<{
    visible: boolean
  }>()

  const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void
  }>()

  const showModal = computed({
    get: () => props.visible,
    set: (val) => emit('update:visible', val)
  })

  const activeTab = ref('untranslated')
  const reporting = ref(false)
  const settingStore = useSettingStore()

  const hasWebhook = computed(() => !!VITE_FEISHU_WEBHOOK_URL)

  const totalCount = computed(() => translateCollector.getTotalCount())
  const untranslatedCount = computed(() => translateCollector.getUntranslatedCount())
  const translatedCount = computed(() => translateCollector.getTranslatedCount())

  const untranslatedKeys = computed(() => translateCollector.getUntranslatedKeys())
  const translatedKeys = computed(() => translateCollector.getTranslatedKeys())

  function handleClear() {
    translateCollector.clear()
    message.success('已清空收集数据')
  }

  function handleRefresh() {
    // 强制刷新
    showModal.value = false
    setTimeout(() => {
      showModal.value = true
    }, 0)
  }

  function handleCopy() {
    const keys = untranslatedKeys.value.join('\n')
    navigator.clipboard
      .writeText(keys)
      .then(() => {
        message.success(`已复制 ${untranslatedKeys.value.length} 个未翻译字段`)
      })
      .catch(() => {
        message.error('复制失败')
      })
  }

  async function handleReport() {
    if (untranslatedKeys.value.length === 0) {
      message.warning('没有需要上报的未翻译字段')
      return
    }

    reporting.value = true
    try {
      /*await FeishuService.reportToWebhook({
        keys: untranslatedKeys.value.map((key) => ({ key, firstSeen: 0, count: 0 })),
        country: VITE_COUNTRY || 'MY',
        locale: settingStore.locale || 'en',
        timestamp: Date.now()
      })*/

      // 标记已上报
      translateCollector.markAsReported(untranslatedKeys.value)
      message.success('上报成功')
    } catch (error) {
      console.error('上报失败:', error)
      message.error('上报失败，请查看控制台')
    } finally {
      reporting.value = false
    }
  }
</script>

<style lang="less" scoped>
  .collector-content {
    .stats-bar {
      display: flex;
      justify-content: center;
      padding: 16px;
      background: #f5f5f5;
      border-radius: 4px;
      margin-bottom: 16px;
    }

    .action-bar {
      display: flex;
      justify-content: space-between;
      margin-bottom: 16px;
    }

    .mb-16 {
      margin-bottom: 16px;
    }

    .list-section {
      max-height: 400px;
      overflow-y: auto;

      .key-list {
        .key-items {
          .key-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 12px;
            border-bottom: 1px solid #f0f0f0;

            &:last-child {
              border-bottom: none;
            }

            &.translated {
              background: #f6ffed;
            }

            .key-text {
              font-family: monospace;
              word-break: break-all;
            }

            .check-icon {
              color: #52c41a;
              font-size: 16px;
            }
          }
        }
      }
    }
  }
</style>
